// src/components/coins/EducatorPromos.js
//
// The admin's educator promotions (Layout Builder → Rewards & avatar settings
// → 📣 Educator promotions), as popups on the educator dashboard:
//
//   🪙 coin deal      "25% off coin packs" → [Buy coins] opens the Coin Shop
//   🏷️ product code  "TEACH50 — 50% off"  → [Copy] [Shop now] (the code is
//                     remembered, so the checkout page fills it in)
//
// Each shows at most once a day per promotion (and again whenever the admin
// changes it), one at a time.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalPortal from "../common/ModalPortal";
import CoinStore from "./CoinStore";
import { promoLive, useRewardSettings } from "../../services/rewardSettingsService";
import "./coinStore.css";

const dayKey = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const seenKey = (id, version) => `sbl-promo-${id}-${version || 0}-${dayKey()}`;
const wasSeen = (k) => {
  try {
    return Boolean(localStorage.getItem(k));
  } catch {
    return false;
  }
};
const markSeen = (k) => {
  try {
    localStorage.setItem(k, "1");
  } catch {
    /* ignore */
  }
};

const EducatorPromos = () => {
  const { settings, loaded } = useRewardSettings();
  const navigate = useNavigate();
  const [closed, setClosed] = useState([]);
  const [store, setStore] = useState(false);
  const [copied, setCopied] = useState(false);
  const [ready, setReady] = useState(false);

  // let the dashboard (and the daily-coins popup) settle first
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const promos = settings?.promos || {};
  const queue = useMemo(() => {
    if (!loaded) return [];
    return [
      ["coinDeal", promos.coinDeal],
      ["productCode", promos.productCode],
    ]
      .filter(([, p]) => promoLive(p))
      .map(([id, p]) => ({ id, p, key: seenKey(id, promos.version) }))
      .filter((x) => !wasSeen(x.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, promos.version, promos.coinDeal, promos.productCode]);

  const current = queue.find((x) => !closed.includes(x.id));

  const close = () => {
    if (!current) return;
    markSeen(current.key);
    setClosed((c) => [...c, current.id]);
    setCopied(false);
  };

  if (store) return <CoinStore educator onClose={() => setStore(false)} />;
  if (!ready || !current) return null;
  const { id, p } = current;
  const pct = Math.floor(Number(p.percent) || 0);
  const until = p.until ? new Date(`${p.until}T12:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric" }) : "";

  return (
    <ModalPortal>
      <div className="sbl-promo-pop" role="dialog" aria-modal="true" aria-label={p.title} onMouseDown={(e) => e.target === e.currentTarget && close()}>
        <div className={`sbl-promo is-${id}`}>
          <button type="button" className="sbl-promo-x" onClick={close} aria-label="Close">
            ✕
          </button>
          <div className="sbl-promo-burst" aria-hidden="true">
            <span>{pct}%</span>
            <small>OFF</small>
          </div>
          <h2>{p.title}</h2>
          <p>{p.message}</p>
          {id === "productCode" && (
            <div className="sbl-promo-code">
              <code>{p.code}</code>
              <button
                type="button"
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(p.code);
                    setCopied(true);
                  } catch {
                    /* no clipboard */
                  }
                }}
              >
                {copied ? "Copied ✓" : "Copy"}
              </button>
            </div>
          )}
          {until && <p className="sbl-promo-until">Ends {until}</p>}
          <div className="sbl-promo-actions">
            {id === "coinDeal" ? (
              <button
                type="button"
                className="sbl-promo-go"
                onClick={() => {
                  close();
                  setStore(true);
                }}
              >
                🪙 Buy coins
              </button>
            ) : (
              <button
                type="button"
                className="sbl-promo-go"
                onClick={() => {
                  try {
                    localStorage.setItem("sbl-promo-code", p.code);
                  } catch {
                    /* ignore */
                  }
                  close();
                  navigate("/products");
                }}
              >
                🛍️ Shop now
              </button>
            )}
            <button type="button" className="sbl-promo-later" onClick={close}>
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default EducatorPromos;
